import { createSlice } from "@reduxjs/toolkit";
import { authApi } from "./authApi";
import { extractErrorMessage } from "../../../../utils/errorHandler";

/**
 * Auth slice — локальное состояние аутентификации (EyeHub).
 * Заполняется через authApi (initializeAuth/login/logout matchers).
 */

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,

  // UI state
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // initializeAuth
      .addMatcher(authApi.endpoints.initializeAuth.matchPending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.initializeAuth.matchFulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = action.payload.authenticated;
        state.user = action.payload.user;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.initializeAuth.matchRejected, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.error = extractErrorMessage(action.payload, "Ошибка инициализации аутентификации");
      })

      // login (SSO redirect — фактическое заполнение user через initializeAuth)
      .addMatcher(authApi.endpoints.login.matchPending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.login.matchRejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = extractErrorMessage(action.payload, "Ошибка входа в систему");
      })

      // logout
      .addMatcher(authApi.endpoints.logout.matchPending, (state) => {
        state.isLoading = true;
      })
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.logout.matchRejected, (state, action) => {
        state.isLoading = false;
        // Даже при ошибке выхода считаем пользователя вышедшим.
        state.isAuthenticated = false;
        state.user = null;
        state.error = extractErrorMessage(action.payload, "Ошибка выхода из системы");
      });
  },
});

// Actions
export const { clearError } = authSlice.actions;

// Selectors
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsInitialized = (state) => state.auth.isInitialized;
export const selectCurrentUser = (state) => state.auth.user;

// Combined selector used by useAuth hook
export const selectAuthState = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  isInitialized: state.auth.isInitialized,
  user: state.auth.user,
  isLoading: state.auth.isLoading,
  error: state.auth.error,
});

export default authSlice.reducer;
